import { useState, useEffect, useCallback } from 'react'
import {
  Shield, Lock, CheckCircle2, RefreshCw, FileText,
  Activity, Database, Loader, XCircle, ShieldAlert, Key, UserCheck, AlertTriangle, HelpCircle,
  HardDrive, Play, Clock
} from 'lucide-react'
import { getAuditLogs, getSecurityPosture, getBackupStatus, triggerBackup } from '../api'

export default function SecurityAudit() {
  const [auditLogs, setAuditLogs] = useState([])
  const [postureData, setPostureData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [runningScan, setRunningScan] = useState(false)

  const [backupData, setBackupData] = useState(null)
  const [backupLoading, setBackupLoading] = useState(false)
  const [triggeringBackup, setTriggeringBackup] = useState(false)
  const [backupMessage, setBackupMessage] = useState(null)

  const fetchBackup = useCallback(async () => {
    try {
      setBackupLoading(true)
      const data = await getBackupStatus()
      setBackupData(data)
    } catch (err) {
      console.error('Failed to load backup status:', err)
    } finally {
      setBackupLoading(false)
    }
  }, [])

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const [auditData, posture] = await Promise.all([
        getAuditLogs(100),
        getSecurityPosture(),
        fetchBackup(),
      ])
      setAuditLogs(auditData.audit_logs || [])
      setPostureData(posture)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [fetchBackup])

  useEffect(() => { fetchAll() }, [fetchAll])

  const handleTriggerBackup = async () => {
    setTriggeringBackup(true)
    setBackupMessage(null)
    try {
      const res = await triggerBackup()
      setBackupMessage(res.message || 'Backup initiated successfully in background.')
      setTimeout(() => {
        fetchBackup()
        setTriggeringBackup(false)
      }, 3000)
    } catch (err) {
      setBackupMessage(`Backup trigger failed: ${err.message}`)
      setTriggeringBackup(false)
    }
  }

  const formatDuration = (start, end) => {
    if (!start || !end) return '—'
    const s = new Date(start).getTime()
    const e = new Date(end).getTime()
    if (isNaN(s) || isNaN(e) || e < s) return '—'
    const sec = (e - s) / 1000
    return sec < 1 ? '< 1s' : `${sec.toFixed(1)}s`
  }

  const [scanError, setScanError] = useState(null)

  const handleRunScan = async () => {
    setRunningScan(true)
    setScanError(null)
    try {
      const posture = await getSecurityPosture()
      setPostureData(posture)
    } catch (err) {
      setScanError(err.message)
    } finally {
      setRunningScan(false)
    }
  }

  const passCount = postureData?.summary?.pass ?? 0
  const warnCount = postureData?.summary?.warn ?? 0
  const failCount = postureData?.summary?.fail ?? 0
  const totalChecks = (postureData?.checks || []).length

  return (
    <div className="animate-in">
      <div className="flex-between mb-3">
        <div>
          <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: '22px', fontWeight: 800, color: 'var(--ink-900)' }}>
            Cybersecurity & Audit Log Center
          </h2>
          <p style={{ fontSize: '13px', color: 'var(--ink-600)', marginTop: 2 }}>
            Configuration checks verified against this running process, and the audit trail.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleRunScan}
          disabled={runningScan}
          aria-label="Re-evaluate the security posture checks"
        >
          <RefreshCw size={15} aria-hidden="true" className={runningScan ? 'spin' : ''} />
          {runningScan ? 'Evaluating…' : 'Re-evaluate posture'}
        </button>
      </div>

      {scanError && (
        <div
          role="alert"
          style={{
            background: 'var(--brand-coral-bg)',
            border: '1.5px solid var(--border-ink)',
            color: '#9A2C14',
            padding: '12px 16px',
            borderRadius: 8,
            marginBottom: 18,
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          Security posture evaluation failed: {scanError}
        </div>
      )}

      {/* Stat Cards */}
      <div className="stat-grid">
        <div className="stat-card">
          <div className="stat-card-icon blue"><Shield size={20} /></div>
          <div className="stat-card-value">AES-256-GCM</div>
          <div className="stat-card-label">Credential Vault</div>
          <div className="stat-card-delta up"><Lock size={12} /> Active</div>
        </div>

        <div className="stat-card">
          <div className="stat-card-icon green"><CheckCircle2 size={20} /></div>
          <div className="stat-card-value">{loading ? '...' : `${passCount}/${totalChecks || 0}`}</div>
          <div className="stat-card-label">Verified Security Checks Passed</div>
          {failCount > 0 ? (
            <div className="stat-card-delta down"><ShieldAlert size={12} /> {failCount} Action Required</div>
          ) : (
            <div className="stat-card-delta up"><CheckCircle2 size={12} /> Robust Posture</div>
          )}
        </div>

        <div className="stat-card">
          <div className="stat-card-icon purple"><FileText size={20} /></div>
          <div className="stat-card-value">{loading ? '...' : auditLogs.length}</div>
          <div className="stat-card-label">Logged Audit Events (DB)</div>
        </div>

        <div className="stat-card">
          <div className="stat-card-icon yellow"><Activity size={20} /></div>
          <div className="stat-card-value">{loading ? '...' : warnCount}</div>
          <div className="stat-card-label">Configuration Warnings</div>
        </div>
      </div>

      <div className="grid-2-1 mb-3">
        {/* Security Posture Checks Table */}
        <div className="card">
          <div className="card-header">
            <h3>Verified Security Posture Checks</h3>
            {postureData && (
              <span className={`badge ${postureData.overall === 'pass' ? 'badge-ok' : postureData.overall === 'warn' ? 'badge-warn' : 'badge-alarm'}`}>
                {postureData.overall.toUpperCase()} · {passCount} of {totalChecks} PASSED
              </span>
            )}
          </div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '32px' }}>
              <Loader size={24} className="spin" style={{ color: 'var(--brand-blue)' }} />
            </div>
          ) : error ? (
            <div style={{ padding: '16px', textAlign: 'center' }}>
              <XCircle size={24} style={{ color: 'var(--brand-coral)' }} />
              <div className="text-xs text-muted mt-1">{error}</div>
            </div>
          ) : (
            <div style={{ padding: 0, overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Check ID</th>
                    <th>Security Dimension</th>
                    <th>Evidence & Detail</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(postureData?.checks || []).map((test, i) => (
                    <tr key={i}>
                      <td className="font-mono text-xs text-muted" style={{ whiteSpace: 'nowrap' }}>{test.id}</td>
                      <td style={{ fontWeight: 700, color: 'var(--ink-900)', whiteSpace: 'nowrap' }}>{test.name}</td>
                      <td style={{ fontSize: '12px', color: 'var(--ink-700)', maxWidth: 360 }}>
                        <div>{test.evidence}</div>
                        {test.remediation && (
                          <div style={{ fontSize: '11px', color: 'var(--brand-coral)', marginTop: 4, fontStyle: 'italic' }}>
                            Remediation: {test.remediation}
                          </div>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${
                          test.status === 'pass' ? 'badge-ok' :
                          test.status === 'warn' ? 'badge-warn' :
                          test.status === 'fail' ? 'badge-alarm' : 'badge-neutral'
                        }`}>
                          {test.status === 'pass' ? '✓ PASS' :
                           test.status === 'warn' ? '⚠ WARN' :
                           test.status === 'fail' ? '✗ FAIL' : '? UNKNOWN'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* AES-256 Vault Inspector */}
        <div className="card">
          <div className="card-header">
            <h3>AES-256-GCM Vault Architecture</h3>
          </div>
          <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{
              background: 'var(--canvas-bg)',
              padding: '12px 14px',
              borderRadius: 8,
              border: '1px solid var(--border-ink)'
            }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-500)', textTransform: 'uppercase' }}>
                Master Key Strategy
              </span>
              <div className="font-mono" style={{ fontSize: 12, fontWeight: 700, color: 'var(--brand-blue)', marginTop: 2 }}>
                MASTER_ENCRYPTION_KEY (32-Byte Secret)
              </div>
            </div>

            <div style={{
              background: 'var(--canvas-bg)',
              padding: '12px 14px',
              borderRadius: 8,
              border: '1px solid var(--border-ink)'
            }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-500)', textTransform: 'uppercase' }}>
                Cipher Strategy
              </span>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink-900)', marginTop: 2 }}>
                AES-GCM (12-Byte Nonce + Binary Ciphertext)
              </div>
            </div>

            <div style={{
              background: 'var(--canvas-bg)',
              padding: '12px 14px',
              borderRadius: 8,
              border: '1px solid var(--border-ink)'
            }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-500)', textTransform: 'uppercase' }}>
                Zero-Trust Ephemeral Access
              </span>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#047857', marginTop: 2 }}>
                Cross-account STS AssumeRole creates temporary 1-hour session credentials.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Database Backup & DR Replica Card */}
      <div className="card mb-3">
        <div className="card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Database size={18} style={{ color: 'var(--brand-blue)' }} />
            <h3 style={{ margin: 0, fontSize: 16 }}>Database Backup & DR Replica</h3>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {!backupData?.configured ? (
              <span className="badge badge-warn">Replica Unconfigured</span>
            ) : backupData?.last_backup?.status === 'running' || triggeringBackup ? (
              <span className="badge badge-warn" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <RefreshCw size={11} className="spin" /> RUNNING
              </span>
            ) : backupData?.last_backup?.status === 'success' ? (
              <span className="badge badge-ok" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <CheckCircle2 size={11} /> HEALTHY (OK)
              </span>
            ) : backupData?.last_backup?.status === 'failed' ? (
              <span className="badge badge-alarm" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <XCircle size={11} /> FAILED
              </span>
            ) : (
              <span className="badge badge-info">IDLE</span>
            )}

            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleTriggerBackup}
              disabled={triggeringBackup || !backupData?.configured}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '5px 12px' }}
            >
              <Play size={13} className={triggeringBackup ? 'spin' : ''} />
              {triggeringBackup ? 'Running Backup…' : 'Run Backup Now'}
            </button>

            <button
              className="btn-icon"
              title="Refresh backup status"
              onClick={fetchBackup}
              disabled={backupLoading}
            >
              <RefreshCw size={14} className={backupLoading ? 'spin' : ''} />
            </button>
          </div>
        </div>

        <div className="card-body" style={{ padding: '16px 20px' }}>
          {backupMessage && (
            <div
              role="alert"
              style={{
                background: backupMessage.includes('failed') ? 'var(--brand-coral-bg)' : '#f0fdf4',
                border: `1px solid ${backupMessage.includes('failed') ? 'var(--border-ink)' : '#bbf7d0'}`,
                color: backupMessage.includes('failed') ? '#9A2C14' : '#166534',
                padding: '10px 14px',
                borderRadius: 6,
                marginBottom: 16,
                fontSize: 12.5,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              {backupMessage.includes('failed') ? <AlertTriangle size={15} /> : <CheckCircle2 size={15} />}
              {backupMessage}
            </div>
          )}

          {/* DR Summary Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 20 }}>
            <div style={{ background: 'var(--canvas-bg)', padding: '12px 14px', borderRadius: 8, border: '1px solid var(--border-ink)' }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-500)', textTransform: 'uppercase' }}>
                Operational Database
              </span>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-900)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                <HardDrive size={14} style={{ color: 'var(--brand-blue)' }} /> SQLite (Active Engine)
              </div>
              <div style={{ fontSize: 11, color: 'var(--ink-500)', marginTop: 2 }}>
                Primary database for all queries
              </div>
            </div>

            <div style={{ background: 'var(--canvas-bg)', padding: '12px 14px', borderRadius: 8, border: '1px solid var(--border-ink)' }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-500)', textTransform: 'uppercase' }}>
                Disaster Recovery Target
              </span>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--brand-blue)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Database size={14} /> MongoDB Atlas Replica
              </div>
              <div style={{ fontSize: 11, color: 'var(--ink-500)', marginTop: 2 }}>
                {backupData?.configured ? 'Collection prefix: backup_*' : 'Not configured (MONGO_URI)'}
              </div>
            </div>

            <div style={{ background: 'var(--canvas-bg)', padding: '12px 14px', borderRadius: 8, border: '1px solid var(--border-ink)' }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-500)', textTransform: 'uppercase' }}>
                Last Backup Status
              </span>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-900)', marginTop: 4 }}>
                {backupData?.last_backup ? (
                  <>
                    {backupData.last_backup.tables_count ?? 0} tables · {backupData.last_backup.rows_count ?? 0} rows
                  </>
                ) : (
                  'No backups executed yet'
                )}
              </div>
              <div style={{ fontSize: 11, color: 'var(--ink-500)', marginTop: 2 }}>
                {backupData?.last_backup?.completed_at
                  ? `Duration: ${formatDuration(backupData.last_backup.started_at, backupData.last_backup.completed_at)}`
                  : backupData?.last_backup?.started_at ? 'In progress' : 'Awaiting scheduled run'}
              </div>
            </div>

            <div style={{ background: 'var(--canvas-bg)', padding: '12px 14px', borderRadius: 8, border: '1px solid var(--border-ink)' }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-500)', textTransform: 'uppercase' }}>
                Automated Schedule
              </span>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-900)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Clock size={14} style={{ color: '#047857' }} /> Daily at {backupData?.next_scheduled_ist || '02:00'} IST
              </div>
              <div style={{ fontSize: 11, color: 'var(--ink-500)', marginTop: 2 }}>
                Automated background DR replica
              </div>
            </div>
          </div>

          {/* Last 5 Backups History Table */}
          <div>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink-700)', marginBottom: 10 }}>
              Recent Backup History
            </div>
            {(!backupData?.recent_history || backupData.recent_history.length === 0) ? (
              <div style={{ padding: '16px', textAlign: 'center', color: 'var(--ink-500)', fontSize: 12.5, background: 'var(--canvas-bg)', borderRadius: 6 }}>
                No backup records logged yet. Click "Run Backup Now" to perform an initial disaster recovery backup.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="data-table" style={{ fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th>Started At</th>
                      <th>Status</th>
                      <th>Tables</th>
                      <th>Rows</th>
                      <th>Duration</th>
                      <th>Error / Summary</th>
                    </tr>
                  </thead>
                  <tbody>
                    {backupData.recent_history.slice(0, 5).map((log) => (
                      <tr key={log.id}>
                        <td className="font-mono text-xs text-muted" style={{ whiteSpace: 'nowrap' }}>
                          {log.started_at ? new Date(log.started_at).toLocaleString() : '—'}
                        </td>
                        <td>
                          <span
                            className={`badge ${
                              log.status === 'success'
                                ? 'badge-ok'
                                : log.status === 'running'
                                ? 'badge-warn'
                                : 'badge-alarm'
                            }`}
                            style={{ fontSize: 10 }}
                          >
                            {log.status.toUpperCase()}
                          </span>
                        </td>
                        <td className="font-mono">{log.tables_count ?? '—'}</td>
                        <td className="font-mono">{log.rows_count ?? '—'}</td>
                        <td className="font-mono text-muted">
                          {formatDuration(log.started_at, log.completed_at)}
                        </td>
                        <td style={{ color: log.error_message ? '#9A2C14' : 'var(--ink-600)' }}>
                          {log.error_message || 'Completed successfully'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Audit Log Table */}
      <div className="card">
        <div className="card-header">
          <h3>Full System Audit Log (SQLite — audit_logs table)</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span className="badge badge-info">Live DB</span>
            <button className="btn-icon" title="Refresh" onClick={fetchAll}><RefreshCw size={14}/></button>
          </div>
        </div>
        <div style={{ padding: 0, overflowX: 'auto' }}>
          {auditLogs.length === 0 ? (
            <div className="empty-state">
              <Database size={36} style={{ color: 'var(--brand-blue)', margin: '0 auto 10px' }} />
              <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: 16, color: 'var(--ink-900)', marginBottom: 4 }}>No Audit Logs Recorded Yet</h3>
              <p style={{ fontSize: 12.5, color: 'var(--ink-500)' }}>Platform security events will be logged here automatically.</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Action Event</th>
                  <th>Details</th>
                  <th>Initiated By</th>
                </tr>
              </thead>
              <tbody>
                {auditLogs.map((log, i) => (
                  <tr key={i}>
                    <td className="font-mono text-xs text-muted" style={{ whiteSpace: 'nowrap' }}>
                      {/* A row with no timestamp is shown as such. "Just now" made
                          an undated audit record look like it had only just been
                          written. */}
                      {log.timestamp
                        ? new Date(log.timestamp).toLocaleString()
                        : 'No timestamp recorded'}
                    </td>
                    <td>
                      <span className="badge badge-stable" style={{ fontSize: 10 }}>
                        {log.action}
                      </span>
                    </td>
                    <td style={{ fontSize: 12.5, color: 'var(--ink-800)' }}>
                      {log.details}
                    </td>
                    <td>
                      <div style={{ fontWeight: 700, color: 'var(--ink-900)', fontSize: 12.5 }}>
                        {log.user_name || 'System Service'}
                      </div>
                      <div className="font-mono" style={{ fontSize: 11, color: 'var(--ink-500)' }}>
                        {log.user_email || 'daemon@cloudiq.local'} ({log.user_role || 'system'})
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}

